import { useId, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import styles from './Field.module.css';

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
}

export function TextField({
  label,
  hint,
  error,
  hideLabel,
  className,
  ...rest
}: FieldProps & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      <label htmlFor={id} className={hideLabel ? 'visually-hidden' : styles.label}>
        {label}
      </label>
      <input
        id={id}
        className={styles.input}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${id}-desc` : undefined}
        {...rest}
      />
      {(hint || error) && (
        <p id={`${id}-desc`} className={error ? styles.error : styles.hint}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

export function TextArea({
  label,
  hint,
  error,
  hideLabel,
  className,
  maxLength,
  value,
  ...rest
}: FieldProps & TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }) {
  const id = useId();
  const nearLimit = maxLength !== undefined && value.length > maxLength * 0.8;
  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      <label htmlFor={id} className={hideLabel ? 'visually-hidden' : styles.label}>
        {label}
      </label>
      <textarea
        id={id}
        className={styles.input}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint || error ? `${id}-desc` : undefined}
        {...rest}
      />
      {nearLimit && (
        <span className={[styles.counter, value.length > maxLength! && styles.counterOver].filter(Boolean).join(' ')}>
          {value.length.toLocaleString()} / {maxLength!.toLocaleString()}
        </span>
      )}
      {(hint || error) && (
        <p id={`${id}-desc`} className={error ? styles.error : styles.hint}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
}
